/**
 * Redirect Intent Management
 *
 * This module provides functionality to preserve the user's intended destination
 * when they are redirected to login. After successful authentication, the user
 * can be redirected back to their original destination.
 *
 * Usage:
 * 1. Before redirecting to login, call `saveRedirectIntent()` to save current URL
 * 2. After login, call `getAndClearRedirectIntent()` to get the saved URL and clear it
 * 3. Use `useRedirectIntent()` hook for React components
 */

const REDIRECT_INTENT_KEY = 'beelday_redirect_intent'
const REDIRECT_INTENT_TIMESTAMP_KEY = 'beelday_redirect_intent_timestamp'

const INTENT_EXPIRATION_MS = 30 * 60 * 1000

export type RedirectIntent = {
	url: string
	timestamp: number
}

/**
 * Saves the current URL (or specified URL) as the redirect intent.
 * This should be called before redirecting to the login page.
 */
export const saveRedirectIntent = (url?: string): void => {
	try {
		const intentUrl = url || window.location.href
		const intent: RedirectIntent = {
			url: intentUrl,
			timestamp: Date.now(),
		}
		sessionStorage.setItem(REDIRECT_INTENT_KEY, JSON.stringify(intent))
	} catch (error) {
		console.warn('Failed to save redirect intent:', error)
	}
}

/**
 * Retrieves the saved redirect intent if it exists and is not expired.
 * Does NOT clear the intent - use getAndClearRedirectIntent() for that.
 */
export const getRedirectIntent = (): string | null => {
	try {
		const stored = sessionStorage.getItem(REDIRECT_INTENT_KEY)
		if (!stored) return null

		const intent: RedirectIntent = JSON.parse(stored)

		if (Date.now() - intent.timestamp > INTENT_EXPIRATION_MS) {
			clearRedirectIntent()
			return null
		}

		return intent.url
	} catch (error) {
		console.warn('Failed to get redirect intent:', error)
		return null
	}
}

/**
 * Retrieves and clears the saved redirect intent.
 * This should be called after successful login to get the destination URL.
 */
export const getAndClearRedirectIntent = (): string | null => {
	const intent = getRedirectIntent()
	clearRedirectIntent()
	return intent
}

/**
 * Clears the saved redirect intent.
 */
export const clearRedirectIntent = (): void => {
	try {
		sessionStorage.removeItem(REDIRECT_INTENT_KEY)
	} catch (error) {
		console.warn('Failed to clear redirect intent:', error)
	}
}

/**
 * Checks if there is a valid (non-expired) redirect intent saved.
 */
export const hasRedirectIntent = (): boolean => {
	return getRedirectIntent() !== null
}

/**
 * Extracts the pathname from a full URL.
 * Useful when you need just the path portion for React Router navigation.
 */
export const getRedirectIntentPath = (): string | null => {
	const fullUrl = getRedirectIntent()
	if (!fullUrl) return null

	try {
		const url = new URL(fullUrl)
		return url.pathname + url.search + url.hash
	} catch {
		return fullUrl
	}
}

/**
 * Gets and clears the redirect intent, returning just the pathname.
 */
export const getAndClearRedirectIntentPath = (): string | null => {
	const fullUrl = getAndClearRedirectIntent()
	if (!fullUrl) return null

	try {
		const url = new URL(fullUrl)
		return url.pathname + url.search + url.hash
	} catch {
		return fullUrl
	}
}
